import { useTranslation } from "react-i18next";
import { ExternalLink, Building2, ArrowRight, Send } from "lucide-react";
import { StepCard } from "./StepCard";
import { SendTokenForm } from "./SendTokenForm";

interface OffRampUSAProps {
  regionCode?: string;
}

const EXCHANGE_OPTIONS = [
  {
    name: "Coinbase",
    url: "https://www.coinbase.com",
    descriptionKey: "ramp.coinbaseDesc",
    descriptionFallback: "Instant cashout to bank. No conversion fee for USDC.",
    icon: "\u{1F535}",
  },
  {
    name: "Kraken",
    url: "https://www.kraken.com",
    descriptionKey: "ramp.krakenDesc",
    descriptionFallback: "Low-fee withdrawals. Multiple withdrawal methods supported.",
    icon: "\u{1F7E3}",
  },
] as const;

function OffRampUSA({ regionCode }: OffRampUSAProps) {
  const { t } = useTranslation();

  // Determine which tip to show based on region
  const isUS = !regionCode || regionCode === "US" || regionCode === "US_CA" || regionCode === "CA";
  const tipKey = isUS ? "ramp.tipCoinbaseUS" : "ramp.tipCoinbaseGlobal";
  const tipFallback = isUS
    ? "Coinbase converts USDC to USD at no fee, then withdraws to your bank for free via ACH. This is usually the cheapest option."
    : "Coinbase and Kraken support bank withdrawals in most countries via SEPA (Europe), Faster Payments (UK), or local bank transfer. Check your exchange for available withdrawal methods.";

  return (
    <div className="space-y-6">
      <div className="rounded-xl bg-gradient-to-r from-brand-500/10 to-success-500/10 p-4">
        <p
          className="text-sm text-text-secondary"
          dangerouslySetInnerHTML={{ __html: t("ramp.exchangeIntro", "To convert your <strong>USDC</strong> to local currency in your bank account, send your tokens to an exchange and withdraw to your bank.") }}
        />
      </div>

      <div className="space-y-5">
        <StepCard
          step={1}
          title={t("ramp.step1Exchange")}
          description={t("ramp.step1ExchangeDesc")}
          icon={<Building2 className="h-4 w-4 text-brand-500" />}
          action={
            <div className="space-y-2">
              {EXCHANGE_OPTIONS.map((exchange) => (
                <a
                  key={exchange.name}
                  href={exchange.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 rounded-lg border border-border-subtle p-3 transition-colors hover:bg-surface-secondary"
                >
                  <span className="text-xl">{exchange.icon}</span>
                  <div className="flex-1">
                    <span className="text-sm font-medium text-text-primary">
                      {exchange.name}
                    </span>
                    <p className="text-xs text-text-muted">
                      {t(exchange.descriptionKey, exchange.descriptionFallback)}
                    </p>
                  </div>
                  <ExternalLink className="h-4 w-4 text-text-muted" />
                </a>
              ))}
            </div>
          }
        />

        <StepCard
          step={2}
          title={t("ramp.step2ExchangeAddress")}
          description={t("ramp.step2ExchangeAddressDesc")}
          icon={<ArrowRight className="h-4 w-4 text-brand-500" />}
        />

        <StepCard
          step={3}
          title={t("ramp.step3Send")}
          description={t("ramp.step3SendDesc")}
          icon={<Send className="h-4 w-4 text-brand-500" />}
          action={<SendTokenForm title={t("ramp.sendToExchange")} />}
        />

        <StepCard
          step={4}
          title={t("ramp.step4Withdraw")}
          description={t("ramp.step4WithdrawDesc")}
          icon={<Building2 className="h-4 w-4 text-brand-500" />}
        />
      </div>

      <div className="rounded-xl border border-warning-200 bg-warning-50 p-4 dark:border-warning-800 dark:bg-warning-950">
        <h4 className="text-sm font-semibold text-warning-700 dark:text-warning-400">
          {t("ramp.tip")}
        </h4>
        <p className="mt-1 text-xs text-warning-600 dark:text-warning-500">
          {t(tipKey, tipFallback)}
        </p>
      </div>
    </div>
  );
}

export { OffRampUSA };
